import { Customer, CreateCustomerRequest, CreateCampaignRequest, Campaign } from '../types/campaign';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export class ApiService {
  public static async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Authentication required');
        }
        
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Customer API methods
  static async getCustomers(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<{
    success: boolean;
    data: Customer[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);

    const queryString = searchParams.toString();
    const endpoint = `/api/customers${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest(endpoint);
  }

  // Campaign-specific customer search
  static async searchCustomers(query: string): Promise<Customer[]> {
    const response = await this.getCustomers({ search: query, limit: 50 });
    return response.data;
  }

  // Find similar customers for duplicate detection
  static async findSimilarCustomers(name: string, domain: string): Promise<Customer[]> {
    return this.findDuplicateCustomers(domain, name).then(response => response.data);
  }

  static async getCustomerById(id: string): Promise<{
    success: boolean;
    data: Customer;
  }> {
    return this.makeRequest(`/api/customers/${id}`);
  }

  static async createCustomer(customerData: CreateCustomerRequest): Promise<{
    success: boolean;
    data: Customer;
    message: string;
  }> {
    return this.makeRequest('/api/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  static async updateCustomer(id: string, customerData: Partial<CreateCustomerRequest>): Promise<{
    success: boolean;
    data: Customer;
    message: string;
  }> {
    return this.makeRequest(`/api/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  static async deleteCustomer(id: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.makeRequest(`/api/customers/${id}`, {
      method: 'DELETE',
    });
  }

  static async findDuplicateCustomers(domain: string, name?: string): Promise<{
    success: boolean;
    data: Customer[];
  }> {
    const searchParams = new URLSearchParams();
    searchParams.append('domain', domain);
    if (name) searchParams.append('name', name);

    return this.makeRequest(`/api/customers/duplicates?${searchParams.toString()}`);
  }

  static async getCustomerStats(): Promise<{
    success: boolean;
    data: {
      total: number;
      active: number;
      prospects: number;
      inactive: number;
      totalRevenue: number;
      avgDealSize: number;
    };
  }> {
    return this.makeRequest('/api/customers/stats');
  }

  // Campaign API methods
  static async getCampaigns(params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    customerId?: string;
  }): Promise<{
    success: boolean;
    data: Campaign[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.customerId) searchParams.append('customerId', params.customerId);

    const queryString = searchParams.toString();
    const endpoint = `/api/campaigns${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest(endpoint);
  }

  static async getCampaignById(id: string): Promise<{
    success: boolean;
    data: Campaign;
  }> {
    return this.makeRequest(`/api/campaigns/${id}`);
  }

  static async createCampaign(campaignData: CreateCampaignRequest): Promise<{
    success: boolean;
    data: Campaign;
    message: string;
  }> {
    return this.makeRequest('/api/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
  }

  static async updateCampaign(id: string, campaignData: Partial<CreateCampaignRequest>): Promise<{
    success: boolean;
    data: Campaign;
    message: string;
  }> {
    return this.makeRequest(`/api/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(campaignData),
    });
  }

  static async deleteCampaign(id: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.makeRequest(`/api/campaigns/${id}`, {
      method: 'DELETE',
    });
  }

  // Contracts API methods
  static async getContracts(params?: {
    page?: number;
    limit?: number;
    status?: string;
    stage?: string;
    customerId?: string;
  }): Promise<{
    success: boolean;
    data: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.stage) searchParams.append('stage', params.stage);
    if (params?.customerId) searchParams.append('customerId', params.customerId);

    const queryString = searchParams.toString();
    const endpoint = `/api/contracts${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest(endpoint);
  }

  static async getContractById(id: string): Promise<{
    success: boolean;
    data: any;
  }> {
    return this.makeRequest(`/api/contracts/${id}`);
  }

  static async createContract(contractData: any): Promise<{
    success: boolean;
    data: any;
    message: string;
  }> {
    return this.makeRequest('/api/contracts', {
      method: 'POST',
      body: JSON.stringify(contractData),
    });
  }

  static async updateContract(id: string, contractData: any): Promise<{
    success: boolean;
    data: any;
    message: string;
  }> {
    return this.makeRequest(`/api/contracts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(contractData),
    });
  }

  static async deleteContract(id: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.makeRequest(`/api/contracts/${id}`, {
      method: 'DELETE',
    });
  }

  static async getContractStats(): Promise<{
    success: boolean;
    data: {
      totalContracts: number;
      totalValue: number;
      signedContracts: number;
      signedValue: number;
      avgDealSize: number;
      winRate: number;
    };
  }> {
    return this.makeRequest('/api/contracts/stats');
  }

  // Health check
  static async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    environment: string;
    version: string;
  }> {
    return this.makeRequest('/api/health');
  }

  // Company search methods
  static async searchCompanies(searchParams: any): Promise<any> {
    return this.makeRequest('/api/companies/search', {
      method: 'POST',
      body: JSON.stringify(searchParams),
    });
  }

  static async getCompanySuggestions(query: string): Promise<any> {
    return this.makeRequest(`/api/companies/suggestions?q=${encodeURIComponent(query)}`);
  }

  static async saveCompanySearch(searchData: any): Promise<any> {
    return this.makeRequest('/api/companies/saved-searches', {
      method: 'POST',
      body: JSON.stringify(searchData),
    });
  }

  // People search methods
  static async searchPeople(searchParams: any): Promise<any> {
    return this.makeRequest('/api/people/search', {
      method: 'POST',
      body: JSON.stringify(searchParams),
    });
  }

  static async getPeopleSuggestions(query: string): Promise<any> {
    return this.makeRequest(`/api/people/suggestions?q=${encodeURIComponent(query)}`);
  }

  static async savePeopleSearch(searchData: any): Promise<any> {
    return this.makeRequest('/api/people/saved-searches', {
      method: 'POST',
      body: JSON.stringify(searchData),
    });
  }
}

// Generic API service instance for services that need generic HTTP methods
export const apiService = {
  get: async (endpoint: string) => {
    return ApiService.makeRequest(endpoint, { method: 'GET' });
  },
  
  post: async (endpoint: string, data?: any) => {
    return ApiService.makeRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },
  
  put: async (endpoint: string, data?: any) => {
    return ApiService.makeRequest(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },
  
  delete: async (endpoint: string) => {
    return ApiService.makeRequest(endpoint, { method: 'DELETE' });
  },
}; 